// May 2026 — Public landing page for /invite/{TOKEN}.
//
// Backed by GET /api/referral/{token} (public preview) which
// returns ``{valid, plan, duration_months}``. On click of "Create
// Free Account", the token is stashed in ``localStorage`` under
// ``pending_referral_token`` and the user is sent to ``/auth``
// in signup mode. Post-signup activation is wired in App.js
// (``/dashboard`` mount) so it fires regardless of which sub-flow
// (email/password, magic link, Google) finishes the auth.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Gift, Check, Loader2 } from "lucide-react";
import axios from "axios";

const PERKS = [
  "Unlimited DMs",
  "AI reel analytics",
  "Comment-to-DM automation",
  "Link in Bio with email capture",
];

const PLAN_LABELS = {
  starter: "Starter",
  creator: "Creator",
  pro: "Pro",
};

function buildBaseUrl() {
  return process.env.REACT_APP_BACKEND_URL || "";
}

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, error: null, info: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${buildBaseUrl()}/api/referral/${encodeURIComponent(token || "")}`;
        const r = await axios.get(url);
        if (cancelled) return;
        setState({ loading: false, valid: true, error: null, info: r.data });
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || "This invite link is no longer valid or has expired.";
        setState({ loading: false, valid: false, error: detail, info: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const proceedToSignup = () => {
    try {
      // Persist token so post-signup activation can pick it up.
      // Cleared automatically on successful activation in App.js.
      localStorage.setItem("pending_referral_token", (token || "").toUpperCase());
    } catch { /* localStorage unavailable — server-side activation
                    via /api/referral/activate still works once user
                    is signed in. */ }
    navigate("/auth?mode=signup");
  };

  const proceedToSignin = () => {
    try {
      localStorage.setItem("pending_referral_token", (token || "").toUpperCase());
    } catch { /* ignore */ }
    navigate("/auth?mode=signin");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex items-center justify-center px-4 py-12" data-testid="invite-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="text-3xl font-extrabold tracking-tight text-teal-400" data-testid="invite-logo">Tadlo</div>
        </div>

        {state.loading ? (
          <div className="rounded-2xl border border-zinc-800 bg-[#13131A] p-8 text-center" data-testid="invite-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-teal-500" />
            <div className="text-sm text-zinc-400 mt-3">Verifying invite…</div>
          </div>
        ) : !state.valid ? (
          <div className="rounded-2xl border border-zinc-800 bg-[#13131A] p-6 text-center space-y-4" data-testid="invite-invalid">
            <div className="text-4xl">😕</div>
            <h1 className="text-lg font-semibold">Invite no longer valid</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">{state.error}</p>
            <button
              type="button"
              onClick={() => navigate("/auth?mode=signup")}
              data-testid="invite-fallback-signup"
              className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-semibold transition"
            >
              Sign up normally →
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-800 bg-[#13131A] p-6 space-y-5" data-testid="invite-valid">
            <div className="flex items-center gap-2 text-base font-semibold">
              <Gift className="w-5 h-5 text-teal-400" /> You've been invited to Tadlo
            </div>
            <div className="space-y-1">
              <div className="text-xl font-bold leading-tight" data-testid="invite-headline">
                You'll get <span className="text-teal-400">{PLAN_LABELS[state.info?.plan] || state.info?.plan}</span> plan FREE
              </div>
              <div className="text-xl font-bold leading-tight">
                for {state.info?.duration_months} month{state.info?.duration_months > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-zinc-500 mt-2">No credit card required.</div>
            </div>

            <div className="border-t border-zinc-800 pt-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">What's included</div>
              <ul className="space-y-1.5">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="button"
              onClick={proceedToSignup}
              data-testid="invite-create-account-cta"
              className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-semibold transition"
            >
              Create Free Account →
            </button>

            <div className="text-center text-xs text-zinc-500">
              Already have an account?{" "}
              <Link
                to="/auth?mode=signin"
                onClick={proceedToSignin}
                data-testid="invite-signin-link"
                className="text-teal-400 hover:text-teal-300 font-medium"
              >
                Sign in →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
